import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { Button, Heading, Card, Input } from '@enterprise-ui/canvas-ui-react';
import classnames from 'classnames';
import React, { useState, useEffect, useCallback } from 'react';
import boldString from '../utils/boldedTypeahead.js';

var OptionsList = function OptionsList(_ref) {
  var location = _ref.location,
    id = _ref.id,
    dataTestId = _ref['data-testid'],
    _ref$grouped = _ref.grouped,
    grouped = _ref$grouped === void 0 ? false : _ref$grouped,
    optionSize = _ref.optionSize,
    options = _ref.options,
    selectedOption = _ref.selectedOption,
    isLoading = _ref.isLoading,
    isShowingOptions = _ref.isShowingOptions,
    onOptionClick = _ref.onOptionClick,
    optionsListNoMinWidth = _ref.optionsListNoMinWidth,
    _ref$checkedOptions = _ref.checkedOptions,
    checkedOptions = _ref$checkedOptions === void 0 ? [] : _ref$checkedOptions,
    multiselect = _ref.multiselect,
    renderOption = _ref.renderOption,
    noResultsMessage = _ref.noResultsMessage,
    _ref$as = _ref.as,
    As = _ref$as === void 0 ? Card : _ref$as,
    className = _ref.className,
    searchText = _ref.searchText,
    filter = _ref.filter,
    setSelectedOption = _ref.setSelectedOption;
  var cardClasses = classnames('C-Autocomplete__Options', _defineProperty({}, "--location-".concat(location), location), _defineProperty({}, "--min-width-none", optionsListNoMinWidth), {
    isVisible: isShowingOptions
  }, className, 'C-Autocomplete__List');
  var safeScroll = function safeScroll() {
    var shouldFire = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    return function (listElm) {
      return function (event) {
        if (shouldFire && !event.haltCanvasEvent) {
          var selectedElm = listElm.querySelector('Button.isHover');
          selectedElm === null || selectedElm === void 0 ? void 0 : selectedElm.focus();
        }
      };
    };
  };
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    arrayOption = _useState2[0],
    setArrayOption = _useState2[1];
  var indeterminateState = function indeterminateState(option) {
    if (grouped) {
      return option.label === 'Select All' && (arrayOption === null || arrayOption === void 0 ? void 0 : arrayOption.length) !== (checkedOptions === null || checkedOptions === void 0 ? void 0 : checkedOptions.length) ? true : false;
    }
    return option.label === 'Select All' && (options === null || options === void 0 ? void 0 : options.length) !== (checkedOptions === null || checkedOptions === void 0 ? void 0 : checkedOptions.length) ? true : false;
  };
  useEffect(function () {
    if (grouped) {
      var groupValues = [];
      options.map(function (option) {
        groupValues = groupValues.concat(option.values);
      });
      setArrayOption(groupValues);
    }
  }, [options, grouped]);
  var generateOption = function generateOption(option, index, groupIndex, groupLength) {
    var label = renderOption(option, options);
    var newValue = option.isNew && Array.isArray(checkedOptions) ? !(checkedOptions !== null && checkedOptions !== void 0 && checkedOptions.some(function (checkedValue) {
      return checkedValue.label === option.label;
    })) : option.isNew;
    var isChecked = Array.isArray(checkedOptions) && (checkedOptions === null || checkedOptions === void 0 ? void 0 : checkedOptions.some(function (checkedValue) {
      return checkedValue.label === option.label;
    }));
    var groupCountText = typeof groupLength === 'number' ? "".concat(index + 1, " of ").concat(groupLength, " ") : '';
    var optionItemAriaLabel = newValue ? "Create ".concat(option.label) : "".concat(groupCountText).concat(option.label, " ").concat(isChecked ? 'selected' : 'not selected');
    return /*#__PURE__*/React.createElement("li", {
      key: _typeof(option.id) !== undefined ? option.id : index
    }, /*#__PURE__*/React.createElement(Button, {
      "aria-label": optionItemAriaLabel,
      role: "option",
      className: classnames('--embeddedInComponent', {
        isHover: !!selectedOption ? selectedOption.id === option.id : false
      }),
      id: option.id,
      "data-testid": dataTestId ? "".concat(dataTestId).concat(groupIndex !== false ? "_group_0".concat(groupIndex) : '', "_option_0").concat(index) : null,
      size: optionSize,
      "data-test": "".concat(id, "___option_#").concat(index),
      disabled: option.disabled,
      onClick: multiselect ? newValue ? onOptionClick(option) : null : onOptionClick(option),
      onChange: multiselect ? option.label === 'Select All' ? grouped ? onOptionClick(arrayOption) : onOptionClick(options) : onOptionClick(option) : null,
      onMouseOver: function onMouseOver() {
        setSelectedOption(option);
      }
    }, multiselect ? newValue ? label : /*#__PURE__*/React.createElement(Input.Checkbox, {
      label: searchText ? /*#__PURE__*/React.createElement("span", null, boldString(label, filter, searchText)) : label,
      indeterminate: indeterminateState(option),
      disabled: option.disabled,
      checked: isChecked
    }) : searchText ? /*#__PURE__*/React.createElement("span", null, boldString(label, filter, searchText)) : label));
  };
  var onScroll = useCallback(function (event) {
    var _event$view;
    var element = (_event$view = event.view) === null || _event$view === void 0 ? void 0 : _event$view.document;
    var listElm = element === null || element === void 0 ? void 0 : element.getElementById("ariaControls_".concat(id));
    if (listElm) {
      return safeScroll(isShowingOptions)(listElm)(event);
    }
    return;
  }, [id, isShowingOptions]);
  useEffect(function () {
    window.addEventListener('keyup', onScroll);
    window.addEventListener('keydown', onScroll);
    return function () {
      // returned function will be called on component unmount
      window.removeEventListener('keyup', onScroll);
      window.removeEventListener('keydown', onScroll);
    };
  });
  return /*#__PURE__*/React.createElement(As, {
    as: "ul",
    "data-testid": dataTestId ? "".concat(dataTestId, "_optionsList") : null,
    className: cardClasses,
    elevation: 2,
    "data-test": "optionsList",
    role: "listbox",
    id: "ariaControls_".concat(id)
  }, options.length === 0 ? /*#__PURE__*/React.createElement("li", {
    key: "isLoading"
  }, /*#__PURE__*/React.createElement(Button, {
    role: "option",
    disabled: true,
    className: classnames(_defineProperty({}, "disabled", isLoading || noResultsMessage))
  }, isLoading ? 'Loading Results' : noResultsMessage)) : /*#__PURE__*/React.createElement(React.Fragment, null, grouped ? options.map(function (group, groupIndex) {
    return /*#__PURE__*/React.createElement("li", {
      key: "group_0".concat(groupIndex)
    }, /*#__PURE__*/React.createElement("ul", {
      className: "C-Autocomplete__List"
    }, group.name !== 'Ungrouped' && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(Heading, {
      className: "hc-ph-xs hc-pv-dense",
      size: 6,
      role: "heading"
    }, group.name)), group.values && group.values.map(function (option, index) {
      var _group$values;
      return generateOption(option, index, groupIndex, (_group$values = group.values) === null || _group$values === void 0 ? void 0 : _group$values.length);
    })));
  }) : options.map(function (option, index) {
    return generateOption(option, index, false);
  })));
};
OptionsList.displayName = 'OptionsList';

export { OptionsList as default };
